var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useCallback, useRef, useEffect, useContext, useMemo, memo, forwardRef, useImperativeHandle } from 'react';
import { cloneDeep } from 'lodash-es';
import classNames from 'classnames';
import BaseTableComponent, { cleanSort, processColumns } from '../Common/BaseTableComponent';
import PaginationComponent from '../../Pagination/PaginationComponent';
import useToast from '@notifications/Toast/useToast';
import { LoadingSpinner } from '@widget/loaders';
import SearchBox from '@widget/forms/inputs/SearchBox';
import useDebounce from '@hooks/useDebounce';
import Checkbox from '@widget/forms/inputs/Checkbox';
import Badge from '@widget/other/Badge';
import Input from '@widget/forms/inputs/Input';
import Button from '@widget/buttons/Button';
import useAlert from '@notifications/Alerts/useAlerts';
import { ObjectDetailsPanel } from './ObjectDetailsPanel';
import { ViewsList } from './ViewsList';
import { cleanFilters, FilterView, getFilterCount } from './FilterView';
import { PipelineRefineModal } from './PipelineRefineModal';
import { UXPContext } from '@core/context/Context';
import { hasValue, simpleHash, textSearch } from '@utils';
import { useExportData } from '@hooks/useExportData';
import { useRouterContext, useSearchParams } from '@hooks/useRouteHandlers';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
function hashPipeline(pipeline) {
    var _a;
    return (_a = simpleHash(JSON.stringify(pipeline), '', { base: 36, ignorePrefix: true })) !== null && _a !== void 0 ? _a : '';
}
const ObjectSearchComponentBase = forwardRef((_a, ref) => {
    var _b, _c;
    var { data: propData, columns: propColumns, defaultActionColumns, pageSize: initialPageSize, total: propTotal, idField, nameField, typeField, onSelect, selected = [], minCellWidth = 100, detailsPanel, onClickRow, views, filters: filterConfig, collapsedWidth = '50%', appendToURL = true, clearParamsOnSelect, urlParams: propUrlParams, onFilterPanelToggle } = _a, props = __rest(_a, ["data", "columns", "defaultActionColumns", "pageSize", "total", "idField", "nameField", "typeField", "onSelect", "selected", "minCellWidth", "detailsPanel", "onClickRow", "views", "filters", "collapsedWidth", "appendToURL", "clearParamsOnSelect", "urlParams", "onFilterPanelToggle"]);
    const context = useContext(UXPContext);
    const toast = useToast();
    const alerts = useAlert();
    const { navigate, generateURL } = useRouterContext();
    // Merge URL params with defaults.
    // Default changed from 'key' to 'objectKey' — 'key' is a React reserved prop and
    // must not flow through as additionalProps to createElement (causes widget remounts).
    const urlParams = useMemo(() => ({
        key: (propUrlParams === null || propUrlParams === void 0 ? void 0 : propUrlParams.key) || 'objectKey',
        detailsTab: (propUrlParams === null || propUrlParams === void 0 ? void 0 : propUrlParams.detailsTab) || 'dt',
        additionalDetailsTab: (propUrlParams === null || propUrlParams === void 0 ? void 0 : propUrlParams.additionalDetailsTab) || 'ad',
        name: (propUrlParams === null || propUrlParams === void 0 ? void 0 : propUrlParams.name) || 'objectID',
        type: (propUrlParams === null || propUrlParams === void 0 ? void 0 : propUrlParams.type) || 'objectType',
    }), [propUrlParams]);
    const searchParams = useSearchParams();
    // Backward compat: fall back to the legacy 'key' param when using the default 'objectKey'.
    // This ensures old bookmarked URLs (?key=123) still open the details panel correctly.
    const key = searchParams[urlParams.key] || (urlParams.key === 'objectKey' ? searchParams['key'] : null);
    const searchConfigRef = useRef(props === null || props === void 0 ? void 0 : props.search);
    searchConfigRef.current = props === null || props === void 0 ? void 0 : props.search;
    const containerRef = useRef(null);
    const listContainerRef = useRef(null);
    const hasFiltersChangedRef = useRef(false);
    const hasSortChangedRef = useRef(false);
    const hasColumnsChangedRef = useRef(false);
    const actionsDropdwonRef = useRef(null);
    const sortDropdwonRef = useRef(null);
    const columnsDropdwonRef = useRef(null);
    const viewsListRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [listContainerWidth, setListContainerWidth] = useState(0);
    const [allColumns, setAllColumns] = useState([]);
    const [processedColumns, setProcessedColumns] = useState(null);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(initialPageSize || 10);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');
    const [filters, setFilters] = useState(null);
    const [sort, setSort] = useState({});
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    useEffect(() => {
        onFilterPanelToggle === null || onFilterPanelToggle === void 0 ? void 0 : onFilterPanelToggle(showFilterPanel);
    }, [showFilterPanel, onFilterPanelToggle]);
    const [showViewsList, setShowViewsList] = useState(false);
    const [resizedColumns, setResizedColumns] = useState({});
    const [viewInstance, setViewInstance] = useState(null);
    const [viewAction, setViewAction] = useState(null);
    const [viewName, setViewName] = useState('');
    const [selectedView, setSelectedView] = useState(null);
    // Advanced filter state
    const [filterMode, setFilterMode] = useState('simple');
    const [advancedQuestion, setAdvancedQuestion] = useState('');
    const [advancedPipeline, setAdvancedPipeline] = useState(null);
    const [advancedData, setAdvancedData] = useState(null);
    const [extraColumns, setExtraColumns] = useState([]);
    const [advancedLoading, setAdvancedLoading] = useState(false);
    const [advancedError, setAdvancedError] = useState(null);
    const [originalPipeline, setOriginalPipeline] = useState(null);
    const [originalPipelineHash, setOriginalPipelineHash] = useState(null);
    const [pipelineHash, setPipelineHash] = useState(null);
    const [showRefineModal, setShowRefineModal] = useState(false);
    const [pipelineRefined, setPipelineRefined] = useState(false);
    const debouncedQuery = useDebounce(query, 300);
    const [debouncedFilters, , immediateUpdateFilters] = useDebounce(filters, 300, true);
    const [debouncedSort, , immediateUpdateSort] = useDebounce(sort, 300, true);
    const isDetailsPanelShowing = useMemo(() => !!detailsPanel && hasValue(viewInstance), [detailsPanel, viewInstance]);
    const { exportData } = useExportData();
    const hasViews = useMemo(() => {
        var _a;
        return !!views && (hasValue(views.listId) || !!((_a = views.defaultViews) === null || _a === void 0 ? void 0 : _a.length));
    }, [views]);
    const hasDefaultViews = useMemo(() => {
        var _a;
        return hasViews && !!((_a = views.defaultViews) === null || _a === void 0 ? void 0 : _a.length);
    }, [views, hasViews]);
    const hasViewSelected = !hasDefaultViews || (hasDefaultViews && !!selectedView);
    const exportCurrentView = useCallback(() => {
        const exportColumns = Object.values(processedColumns).filter(c => !c.id.startsWith('__')).map(c => ({
            id: c.id,
            label: typeof c.label == 'string' ? c.label : (c === null || c === void 0 ? void 0 : c.exportLabel) || c.id,
            formatter: c.formatter || null
        }));
        return exportData({
            data: propData,
            total: total,
            columns: exportColumns,
            onProgress(progress) {
                console.info('exporting current view', progress);
            },
            fileName: (selectedView === null || selectedView === void 0 ? void 0 : selectedView.name) || (typeof (props === null || props === void 0 ? void 0 : props.title) == 'string' && hasValue(props === null || props === void 0 ? void 0 : props.title) ? props.title : 'Export')
        });
    }, [exportData, propData, debouncedFilters, debouncedQuery, debouncedSort, total, selectedView, props === null || props === void 0 ? void 0 : props.title]);
    const discoverExtraColumns = useCallback((rows) => {
        if (!rows || rows.length === 0)
            return [];
        const existingIds = new Set(propColumns.map(c => c.id));
        return Object.keys(rows[0])
            .filter(k => !existingIds.has(k))
            .map(k => ({ id: k, label: k }));
    }, [propColumns]);
    const runAdvancedSearch = useCallback((question) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        if (!((_a = filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.advanced) === null || _a === void 0 ? void 0 : _a.baseDataSource) || !question.trim())
            return;
        setAdvancedLoading(true);
        setAdvancedError(null);
        try {
            const qe = (_c = (_b = window.QueryBuilderComponents) === null || _b === void 0 ? void 0 : _b.useQueryEngineService) === null || _c === void 0 ? void 0 : _c.call(_b, context);
            if (!qe) {
                setAdvancedError(context.$L('uxp-core.text.query-engine-unavailable'));
                return;
            }
            const columnIds = propColumns.map(c => c.id);
            const requiredFields = columnIds.includes(idField) ? columnIds : [...columnIds, idField];
            const result = yield qe.aiSearch({
                question,
                baseDataSource: filterConfig.advanced.baseDataSource,
                requiredFields,
                execute: true,
            });
            const pipeline = result === null || result === void 0 ? void 0 : result.pipeline;
            // Invalid pipeline — engine returned a clarification question instead of sources/steps
            if (!(pipeline === null || pipeline === void 0 ? void 0 : pipeline.sources) || !(pipeline === null || pipeline === void 0 ? void 0 : pipeline.steps)) {
                setAdvancedError(context.$L('uxp-core.text.advanced-filter-no-results'));
                return;
            }
            const rows = (result === null || result === void 0 ? void 0 : result.result) || [];
            const hash = hashPipeline(pipeline);
            setAdvancedPipeline(pipeline);
            setOriginalPipeline(pipeline);
            setAdvancedData(rows);
            setExtraColumns(discoverExtraColumns(rows));
            setPipelineHash(hash);
            setOriginalPipelineHash(hash);
            setPipelineRefined(false);
            setPage(1);
            hasFiltersChangedRef.current = true;
        }
        catch (e) {
            setAdvancedError(context.$L('uxp-core.text.advanced-filter-no-results'));
            console.error('Advanced search error:', e);
        }
        finally {
            setAdvancedLoading(false);
        }
    }), [context, propColumns, filterConfig, discoverExtraColumns, toast]);
    const runSavedPipeline = useCallback((pipeline) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        if (!pipeline)
            return;
        setAdvancedLoading(true);
        try {
            const qe = (_b = (_a = window.QueryBuilderComponents) === null || _a === void 0 ? void 0 : _a.useQueryEngineService) === null || _b === void 0 ? void 0 : _b.call(_a, context);
            if (!qe) {
                toast.error('Query Engine service is not available.');
                return;
            }
            const execResult = yield qe.executeQuery(pipeline);
            const rows = ((_c = execResult === null || execResult === void 0 ? void 0 : execResult.data) === null || _c === void 0 ? void 0 : _c.result) || [];
            setAdvancedData(rows);
            setExtraColumns(discoverExtraColumns(rows));
            setPage(1);
        }
        catch (e) {
            toast.error('Failed to load saved query. Please try again.');
            console.error('Run saved pipeline error:', e);
        }
        finally {
            setAdvancedLoading(false);
        }
    }), [context, discoverExtraColumns, toast]);
    const handleRefinementApplied = useCallback((newPipeline) => {
        const newHash = hashPipeline(newPipeline);
        const isChanged = newHash !== originalPipelineHash;
        if (isChanged) {
            setAdvancedPipeline(newPipeline);
            setPipelineHash(newHash);
            runSavedPipeline(newPipeline);
            setPipelineRefined(true);
            hasFiltersChangedRef.current = true;
        }
        setShowRefineModal(false);
    }, [originalPipelineHash, runSavedPipeline]);
    const handleRestoreOriginal = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!originalPipeline)
            return;
        const confirmed = yield alerts.confirm({
            title: context.$L('uxp-core.text.restore-original-pipeline-confirm-title'),
            content: context.$L('uxp-core.text.restore-original-pipeline-confirm-content'),
        });
        if (!confirmed)
            return;
        setAdvancedPipeline(originalPipeline);
        setPipelineHash(originalPipelineHash);
        setPipelineRefined(false);
        runSavedPipeline(originalPipeline);
        hasFiltersChangedRef.current = true;
    }), [originalPipeline, originalPipelineHash, runSavedPipeline, alerts, context]);
    const oscDetails = useMemo(() => ({
        selectedView: selectedView,
        page: page,
        pageSize: pageSize,
        filters: debouncedFilters,
        sort: debouncedSort,
        query: debouncedQuery,
        selectedColumns: selectedColumns,
        total: total
    }), [selectedView, page, pageSize, debouncedFilters, debouncedQuery, debouncedSort, selectedColumns, total]);
    useEffect(() => {
        if (appendToURL) {
            setViewInstance(key || null);
        }
    }, [key, appendToURL]);
    // Effect to observe container widths for responsive column calculations
    useEffect(() => {
        let timeoutId;
        const observer = new ResizeObserver(([entry]) => {
            const newWidth = Math.round(entry.contentRect.width);
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                setContainerWidth(prev => {
                    // Only update if difference is more than 5px
                    if (Math.abs(prev - newWidth) > 5) {
                        return newWidth;
                    }
                    return prev;
                });
            }, 100);
        });
        if (containerRef.current)
            observer.observe(containerRef.current);
        return () => {
            clearTimeout(timeoutId);
            observer.disconnect();
        };
    }, []);
    useEffect(() => {
        let timeoutId;
        const observer = new ResizeObserver(([entry]) => {
            const newWidth = Math.round(entry.contentRect.width);
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                setListContainerWidth(prev => {
                    // Only update if difference is more than 5px
                    if (Math.abs(prev - newWidth) > 5) {
                        return newWidth;
                    }
                    return prev;
                });
            }, 100);
        });
        if (listContainerRef.current)
            observer.observe(listContainerRef.current);
        return () => {
            clearTimeout(timeoutId);
            observer.disconnect();
        };
    }, []);
    const applyQueryAndFilters = useCallback((data, query, filters) => {
        var _a;
        let result = cloneDeep(data);
        if (!result || result.length === 0)
            return [];
        const searchConfig = searchConfigRef.current;
        // query
        if (hasValue(query) && (searchConfig === null || searchConfig === void 0 ? void 0 : searchConfig.enable) && ((_a = searchConfig === null || searchConfig === void 0 ? void 0 : searchConfig.fields) === null || _a === void 0 ? void 0 : _a.length) > 0) {
            result = result.filter(r => (searchConfig.fields || []).some(f => textSearch(r === null || r === void 0 ? void 0 : r[f], query, true)));
        }
        // filters
        if ((filters === null || filters === void 0 ? void 0 : filters.filters) && Object.keys(filters.filters).length > 0) {
            result = result.filter(r => Object.entries(filters.filters).every(([key, value]) => r[key] === value));
        }
        return result;
    }, []);
    const filterDataFromArray = useCallback((_data, _page, _pageSize, _query, _filters, _sort) => {
        let result = applyQueryAndFilters(_data, _query, _filters);
        // sort
        if (_sort && Object.keys(_sort).length > 0) {
            result.sort((a, b) => {
                for (const [k, o] of Object.entries(_sort)) {
                    if (o == 0 || a[k] === b[k])
                        continue;
                    return typeof a[k] === 'string'
                        ? o * a[k].localeCompare(b[k])
                        : o * (a[k] > b[k] ? 1 : -1);
                }
                return 0;
            });
        }
        // pagination
        const start = Math.max((_page - 1) * _pageSize, 0);
        const end = start + _pageSize;
        return result.slice(start, end);
    }, [applyQueryAndFilters]);
    useEffect(() => {
        var _a;
        if (filterMode === 'advanced') {
            setTotal((_a = advancedData === null || advancedData === void 0 ? void 0 : advancedData.length) !== null && _a !== void 0 ? _a : 0);
            return;
        }
        if (typeof propTotal === 'function') {
            propTotal(debouncedQuery, debouncedFilters).then(setTotal).catch(console.error);
        }
        else if (Array.isArray(propData)) {
            const filtered = applyQueryAndFilters(propData, debouncedQuery, debouncedFilters);
            setTotal(filtered.length);
        }
        else if (typeof propTotal === 'number') {
            setTotal(propTotal);
        }
    }, [propTotal, propData, debouncedQuery, debouncedFilters, applyQueryAndFilters, filterMode, advancedData]);
    const loadData = useCallback((...args_1) => __awaiter(void 0, [...args_1], void 0, function* (silent = false) {
        if (!hasViewSelected)
            return;
        if (filterMode === 'advanced') {
            if (!advancedData)
                return;
            const start = (page - 1) * pageSize;
            setData(advancedData.slice(start, start + pageSize));
            return;
        }
        if (!silent)
            setLoading(true);
        try {
            if (typeof propData === 'function') {
                const res = yield propData(page, pageSize, debouncedQuery, debouncedFilters, debouncedSort);
                setData((res === null || res === void 0 ? void 0 : res.items) || []);
            }
            else {
                const data = filterDataFromArray(propData || [], page, pageSize, debouncedQuery, debouncedFilters, debouncedSort);
                setData([...data]);
            }
        }
        catch (e) {
            toast.error('Unable to get data. Something went wrong.');
            console.error('Unable to get data:', e);
        }
        finally {
            if (!silent)
                setLoading(false);
        }
    }), [page, pageSize, propData, debouncedQuery, debouncedFilters, debouncedSort, filterDataFromArray, hasViewSelected, filterMode, advancedData]);
    useEffect(() => {
        loadData();
    }, [loadData]);
    useImperativeHandle(ref, () => ({
        export: () => exportCurrentView(),
        getDetails: () => oscDetails,
        refreshCurrentPage: () => loadData(),
        silentRefreshCurrentPage: () => loadData(true),
        setPageData: (items) => setData(items),
        getCurrentPageData: () => data,
        getFilters: () => filters,
        applyFilters: (f) => {
            if (!f || typeof f !== 'object' || typeof f.filters !== 'object')
                return;
            setFilters(cleanFilters(f));
        },
        isFilterPanelOpen: () => showFilterPanel,
    }), [exportCurrentView, oscDetails, loadData, data, filters, showFilterPanel]);
    // Handlers for row and column interactions
    const handleColumnResize = useCallback((id, newWidth) => {
        setProcessedColumns(prev => {
            if (!(prev === null || prev === void 0 ? void 0 : prev[id]))
                return prev;
            const column = prev[id];
            const width = hasValue(column.maxWidth)
                ? Math.min(column.maxWidth, Math.max(newWidth, column.minWidth || 0))
                : Math.max(newWidth, column.minWidth || 0);
            setResizedColumns(prev => (Object.assign(Object.assign({}, prev), { [id]: width })));
            if (width === column.width)
                return prev;
            return Object.assign(Object.assign({}, prev), { [id]: Object.assign(Object.assign({}, column), { width }) });
        });
    }, []);
    const isRowSelected = useCallback((item) => {
        if (selected.some(s => (s === null || s === void 0 ? void 0 : s[idField]) === (item === null || item === void 0 ? void 0 : item[idField])))
            return true;
        if (isDetailsPanelShowing && String(item === null || item === void 0 ? void 0 : item[idField]) === String(viewInstance))
            return true;
        return false;
    }, [selected, idField, isDetailsPanelShowing, viewInstance]);
    const handleRowSelect = useCallback((item) => {
        const updatedSelection = isRowSelected(item)
            ? selected.filter(s => (s === null || s === void 0 ? void 0 : s[idField]) !== (item === null || item === void 0 ? void 0 : item[idField]))
            : [...selected, item];
        onSelect === null || onSelect === void 0 ? void 0 : onSelect(updatedSelection);
    }, [selected, isRowSelected, onSelect, idField]);
    const handleRowClick = useCallback((e, item) => {
        if (onClickRow) {
            onClickRow(e, item);
        }
        else if (detailsPanel) {
            if (appendToURL) {
                const extraParams = {};
                if (nameField && item[nameField] != null)
                    extraParams[urlParams.name] = String(item[nameField]);
                if (typeField && item[typeField] != null)
                    extraParams[urlParams.type] = String(item[typeField]);
                navigate(generateURL({ searchParams: Object.assign({ [urlParams.key]: item[idField] }, extraParams), removeParams: clearParamsOnSelect }));
            }
            else {
                setViewInstance(item[idField]);
            }
        }
    }, [onClickRow, detailsPanel, appendToURL, urlParams, idField, nameField, typeField]);
    const onSelectRows = useCallback((rows) => {
        onSelect === null || onSelect === void 0 ? void 0 : onSelect(rows);
    }, [onSelect]);
    // Effect to construct the list of all possible columns
    useEffect(() => {
        var _a, _b, _c, _d, _e;
        const _allColumns = [];
        if (((_a = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.actionsColumn) === null || _a === void 0 ? void 0 : _a.enable) && !isDetailsPanelShowing)
            _allColumns.push({ id: '__actionsColumn__', label: 'Actions' });
        if ((_b = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.expandColumn) === null || _b === void 0 ? void 0 : _b.enable)
            _allColumns.push({ id: '__expandColumn__', label: 'Expand' });
        if (((_c = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.selectColumn) === null || _c === void 0 ? void 0 : _c.enable) && !isDetailsPanelShowing)
            _allColumns.push({ id: '__selectColumn__', label: 'Select' });
        _allColumns.push(...(propColumns || []));
        // Append extra columns returned by advanced AI search (de-duplicated against propColumns)
        if (extraColumns.length > 0) {
            const existingIds = new Set(propColumns.map(c => c.id));
            _allColumns.push(...extraColumns.filter(c => !existingIds.has(c.id)));
        }
        if (((_d = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.editColumn) === null || _d === void 0 ? void 0 : _d.enable) && !isDetailsPanelShowing)
            _allColumns.push({ id: '__editColumn__', label: 'Edit' });
        if (((_e = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.deleteColumn) === null || _e === void 0 ? void 0 : _e.enable) && !isDetailsPanelShowing)
            _allColumns.push({ id: '__deleteColumn__', label: 'Delete' });
        setAllColumns(_allColumns);
    }, [propColumns, defaultActionColumns, isDetailsPanelShowing, extraColumns]);
    const sortableColumns = useMemo(() => {
        return ((selectedColumns === null || selectedColumns === void 0 ? void 0 : selectedColumns.length) > 0 ? allColumns.filter(c => selectedColumns.includes(c.id)) : allColumns)
            .filter(c => !c.id.startsWith('__') && c.isSortable);
    }, [allColumns, selectedColumns]);
    const multiColumnSortEnabled = useMemo(() => {
        var _a, _b, _c, _d, _e;
        return (((_a = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.actionsColumn) === null || _a === void 0 ? void 0 : _a.enable) || false) && (((_c = (_b = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.actionsColumn) === null || _b === void 0 ? void 0 : _b.headerActions) === null || _c === void 0 ? void 0 : _c.sort) || false) && (((_e = (_d = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.actionsColumn) === null || _d === void 0 ? void 0 : _d.headerActions) === null || _e === void 0 ? void 0 : _e.multiColumnSort) || false);
    }, [defaultActionColumns]);
    const handleSort = useCallback((value) => {
        setSort(prev => {
            if (typeof value == 'string') {
                const currentState = (prev === null || prev === void 0 ? void 0 : prev[value]) || 0;
                const newState = currentState === 0 ? 1 : currentState === 1 ? -1 : 0;
                return cleanSort(Object.assign(multiColumnSortEnabled ? prev : {}, { [value]: newState }));
            }
            else
                return cleanSort(value); // here from base table sorting is handled
        });
        hasSortChangedRef.current = true;
        setPage(1);
    }, [multiColumnSortEnabled]);
    // Memoize the calculation of default action columns to avoid re-computation
    const memoizedDefaultActionColumns = useMemo(() => {
        var _a, _b, _c, _d, _e;
        const dacs = {};
        if (isDetailsPanelShowing)
            return dacs;
        if ((_a = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.editColumn) === null || _a === void 0 ? void 0 : _a.enable)
            dacs.editColumn = Object.assign(Object.assign({}, defaultActionColumns.editColumn), { width: 60 });
        if ((_b = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.deleteColumn) === null || _b === void 0 ? void 0 : _b.enable)
            dacs.deleteColumn = Object.assign(Object.assign({}, defaultActionColumns.deleteColumn), { width: 60 });
        if ((_c = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.selectColumn) === null || _c === void 0 ? void 0 : _c.enable)
            dacs.selectColumn = Object.assign(Object.assign({}, defaultActionColumns.selectColumn), { width: 40, onSelectAll: (selected) => onSelectRows(selected ? data : []) });
        if ((_d = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.expandColumn) === null || _d === void 0 ? void 0 : _d.enable)
            dacs.expandColumn = Object.assign(Object.assign({}, defaultActionColumns.expandColumn), { width: 40 });
        if ((_e = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.actionsColumn) === null || _e === void 0 ? void 0 : _e.enable) {
            const headerActions = [];
            const { customiseColumns, clipOverflowText, sort: enableSort } = defaultActionColumns.actionsColumn.headerActions || {};
            if (customiseColumns) {
                headerActions.push({
                    label: context.$L('uxp-core.text.customise-columns'),
                    icon: 'fal columns',
                    children: () => (_jsx("div", { className: "uxpcore_actions_list", children: allColumns.filter(c => !c.id.startsWith('__') && hasValue(c.label)).map(col => (_jsx("div", { className: "uxpcore_actions_list__action", children: _jsx(Checkbox, { checked: selectedColumns.includes(col.id), label: col.label, type: "bordered", onChange: checked => {
                                    setSelectedColumns(prev => checked ? [...prev, col.id] : prev.filter(id => id !== col.id));
                                    hasColumnsChangedRef.current = true;
                                } }) }, col.id))) })),
                    ref: columnsDropdwonRef
                });
            }
            if (clipOverflowText) {
                headerActions.push({
                    label: context.$L('uxp-core.text.clip-overflowing-text'),
                    icon: 'fal cut',
                    children: () => (_jsx("div", { className: "uxpcore_actions_list", children: "Options goes here" }))
                });
            }
            if (enableSort && (sortableColumns === null || sortableColumns === void 0 ? void 0 : sortableColumns.length) > 0) {
                headerActions.push({
                    label: context.$L('uxp-core.text.sort-by'),
                    icon: 'fal sort',
                    children: () => (_jsx("div", { className: "uxpcore_actions_list", children: (sortableColumns || []).map(col => {
                            const sortDir = (sort === null || sort === void 0 ? void 0 : sort[col.id]) || 0;
                            return (_jsxs("div", { className: classNames("uxpcore_actions_list__action", "uxpcore_table__actions-sortaction", { "uxpcore_table__actions-sortaction--active": sortDir !== 0 }), onClick: () => { handleSort(col.id); }, children: [_jsx("div", { className: "uxpcore_actions_list__action-text", children: col.label }), sortDir !== 0 && (_jsx("div", { className: "uxpcore_actions_list__action-icon", children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', sortDir === 1 ? 'caret-up' : 'caret-down'] }) }))] }, col.id));
                        }) })),
                    ref: sortDropdwonRef
                });
            }
            dacs.actionsColumn = Object.assign(Object.assign({}, defaultActionColumns.actionsColumn), { width: 40, headerActions, ref: actionsDropdwonRef });
        }
        return dacs;
    }, [defaultActionColumns, isDetailsPanelShowing, onSelectRows, data, context, allColumns, selectedColumns, sort, sortableColumns, handleSort]);
    // Memoize the selection of active columns based on view/panel state
    const activeColumns = useMemo(() => {
        var _a;
        const showDetailsPanelColumns = isDetailsPanelShowing && !!((_a = detailsPanel === null || detailsPanel === void 0 ? void 0 : detailsPanel.columns) === null || _a === void 0 ? void 0 : _a.length);
        if (showDetailsPanelColumns) {
            return detailsPanel.columns;
        }
        if (selectedColumns.length > 0) {
            const selectedSet = new Set(selectedColumns);
            return [...propColumns, ...extraColumns].filter(c => selectedSet.has(c.id));
        }
        return [...propColumns, ...extraColumns];
    }, [isDetailsPanelShowing, detailsPanel === null || detailsPanel === void 0 ? void 0 : detailsPanel.columns, selectedColumns, propColumns, extraColumns]);
    // Effect to calculate final column set
    useEffect(() => {
        if (listContainerWidth <= 0)
            return;
        let dacs = memoizedDefaultActionColumns;
        if (selectedColumns.length > 0) {
            const selectedSet = new Set(selectedColumns);
            dacs = Object.entries(memoizedDefaultActionColumns).reduce((acc, [key, value]) => {
                if (key === 'actionsColumn' || selectedSet.has(`__${key}__`)) {
                    acc[key] = value;
                }
                return acc;
            }, {});
        }
        const columnsToHide = isDetailsPanelShowing ? (detailsPanel === null || detailsPanel === void 0 ? void 0 : detailsPanel.columnsToHide) || [] : [];
        const processed = processColumns({
            context,
            columns: activeColumns,
            defaultColumns: dacs,
            containerWidth: listContainerWidth,
            minCellWidth,
            columnsToHide: selectedColumns.length > 0 ? [] : columnsToHide,
            selectedCount: selected.length,
            totalCount: data.length,
            resizedColumns
        });
        setProcessedColumns(processed);
    }, [context, listContainerWidth, activeColumns, memoizedDefaultActionColumns, isDetailsPanelShowing, detailsPanel === null || detailsPanel === void 0 ? void 0 : detailsPanel.columnsToHide, minCellWidth, selected.length, data.length, selectedColumns, resizedColumns]);
    // Memoize panel widths
    const widths = useMemo(() => {
        if (!detailsPanel || !isDetailsPanelShowing)
            return { list: '100%', details: '0%' };
        let listWidth = collapsedWidth;
        const rangeRegex = /^(1[5-9]|[2-6][0-9]|7[0-5])%$/;
        if (typeof listWidth === 'number' && containerWidth > 0) {
            listWidth = `${(listWidth / containerWidth) * 100}%`;
        }
        let listPercent = parseFloat(String(listWidth));
        if (!rangeRegex.test(String(listWidth).trim())) {
            listPercent = Math.min(75, Math.max(15, listPercent));
        }
        const detailsPercent = 100 - listPercent;
        return {
            list: `${listPercent}%`,
            details: `${detailsPercent}%`
        };
    }, [collapsedWidth, containerWidth, detailsPanel, isDetailsPanelShowing]);
    // Views and Filters Logic
    const defaultViews = useMemo(() => (views === null || views === void 0 ? void 0 : views.defaultViews) || [], [views === null || views === void 0 ? void 0 : views.defaultViews]);
    const onViewChange = useCallback((view) => {
        var _a, _b, _c, _d, _e, _f, _g;
        setViewAction(null);
        setShowViewsList(false);
        setSelectedView(view);
        setViewName((view === null || view === void 0 ? void 0 : view.name) || '');
        setPageSize(((_a = view === null || view === void 0 ? void 0 : view.configurations) === null || _a === void 0 ? void 0 : _a.pageSize) || initialPageSize);
        const savedMode = ((_b = view === null || view === void 0 ? void 0 : view.configurations) === null || _b === void 0 ? void 0 : _b.filterMode) || 'simple';
        setFilterMode(savedMode);
        if (savedMode === 'advanced' && ((_c = view === null || view === void 0 ? void 0 : view.configurations) === null || _c === void 0 ? void 0 : _c.advancedFilter)) {
            const { question, pipeline, pipelineHash: savedPipelineHash, originalPipeline: savedOriginal, originalPipelineHash: savedHash, isRefined } = view.configurations.advancedFilter;
            const fallbackHash = hashPipeline(pipeline);
            setAdvancedQuestion(question || '');
            setAdvancedPipeline(pipeline);
            setPipelineHash(savedPipelineHash !== null && savedPipelineHash !== void 0 ? savedPipelineHash : fallbackHash);
            setOriginalPipeline(savedOriginal !== null && savedOriginal !== void 0 ? savedOriginal : pipeline);
            setOriginalPipelineHash(savedHash !== null && savedHash !== void 0 ? savedHash : fallbackHash);
            setPipelineRefined(!!isRefined);
            // Clear simple filter state
            setFilters({ filters: {} });
            immediateUpdateFilters({ filters: {} });
            setSelectedColumns([...(((_d = view === null || view === void 0 ? void 0 : view.configurations) === null || _d === void 0 ? void 0 : _d.columns) || [])]);
            setSort({});
            immediateUpdateSort({});
            runSavedPipeline(pipeline);
        }
        else {
            // Restore simple filter state
            setAdvancedData(null);
            setAdvancedPipeline(null);
            setPipelineHash(null);
            setOriginalPipeline(null);
            setOriginalPipelineHash(null);
            setAdvancedQuestion('');
            setPipelineRefined(false);
            setExtraColumns([]);
            const newFilters = cleanFilters(Object.assign({}, (((_e = view === null || view === void 0 ? void 0 : view.configurations) === null || _e === void 0 ? void 0 : _e.filters) || { filters: {} })));
            const newSort = cleanSort(Object.assign({}, (((_f = view === null || view === void 0 ? void 0 : view.configurations) === null || _f === void 0 ? void 0 : _f.sort) || {})));
            setFilters(newFilters);
            immediateUpdateFilters(newFilters);
            setSelectedColumns([...(((_g = view === null || view === void 0 ? void 0 : view.configurations) === null || _g === void 0 ? void 0 : _g.columns) || [])]);
            setSort(newSort);
            immediateUpdateSort(newSort);
        }
    }, [initialPageSize, immediateUpdateFilters, immediateUpdateSort, runSavedPipeline]);
    const addViewsProps = useMemo(() => ({
        enable: views === null || views === void 0 ? void 0 : views.allowToMangeCustomViews,
        onTriggerAdd: () => {
            setShowViewsList(false);
            setViewAction('add');
        },
    }), [views === null || views === void 0 ? void 0 : views.allowToMangeCustomViews]);
    const editViewsProps = useMemo(() => ({
        enable: views === null || views === void 0 ? void 0 : views.allowToMangeCustomViews,
        onTriggerEdit: () => {
            setShowViewsList(false);
            setViewAction('edit');
        },
    }), [views === null || views === void 0 ? void 0 : views.allowToMangeCustomViews]);
    const deleteViewsProps = useMemo(() => ({
        enable: views === null || views === void 0 ? void 0 : views.allowToMangeCustomViews,
    }), [views === null || views === void 0 ? void 0 : views.allowToMangeCustomViews]);
    const renderTitle = useCallback(() => {
        var _a;
        const hasFilters = !!filterConfig && (!!((_a = filterConfig.formFields) === null || _a === void 0 ? void 0 : _a.length) || !!filterConfig.renderFilters || !!filterConfig.advanced);
        const hasTitle = !!props.title;
        return (_jsxs("div", { className: "uxpcore_osc__title_container", children: [hasFilters && (_jsx(FilterView, { isOpen: showFilterPanel, onToggle: () => setShowFilterPanel(p => !p), appliedFilters: filters, onChange: (f) => {
                        hasFiltersChangedRef.current = true;
                        setFilters(cleanFilters(f));
                        setPage(1);
                    }, formFields: filterConfig.formFields || [], renderCustom: filterConfig.renderFilters, getFilterCount: filterConfig.getFilterCount, filterMode: filterMode, onFilterModeChange: (mode) => {
                        setFilterMode(mode);
                        if (mode === 'simple') {
                            setAdvancedData(null);
                            setExtraColumns([]);
                            hasFiltersChangedRef.current = false;
                        }
                        else {
                            setFilters({ filters: {} });
                            immediateUpdateFilters({ filters: {} });
                            // Re-execute the pipeline if one was active
                            if (advancedPipeline) {
                                runSavedPipeline(advancedPipeline);
                            }
                        }
                    }, advancedConfig: filterConfig.advanced, advancedQuestion: advancedQuestion, onAdvancedQuestionChange: (q) => { setAdvancedQuestion(q); setAdvancedError(null); }, onAdvancedSubmit: () => runAdvancedSearch(advancedQuestion), advancedLoading: advancedLoading, hasActivePipeline: !!advancedPipeline, advancedError: advancedError, activePipeline: advancedPipeline, onEditRefinement: () => setShowRefineModal(true), pipelineRefined: pipelineRefined, hasOriginalPipeline: !!originalPipeline && pipelineRefined, onRestoreOriginalPipeline: handleRestoreOriginal })), (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.advanced) && (_jsx(PipelineRefineModal, { show: showRefineModal, pipeline: advancedPipeline, onClose: () => setShowRefineModal(false), onApply: handleRefinementApplied })), hasViews
                    ? (_jsx(ViewsList, { isOpen: showViewsList, onToggle: () => setShowViewsList(p => !p), listId: views.listId, defaultViews: defaultViews, selectedViewId: selectedView === null || selectedView === void 0 ? void 0 : selectedView.id, onChangeView: onViewChange, addViews: addViewsProps, editViews: editViewsProps, deleteViews: deleteViewsProps, ref: viewsListRef, defaultViewId: views === null || views === void 0 ? void 0 : views.defaultviewId }))
                    : (_jsx("div", { className: "uxpcore_osc__title", children: hasTitle ? props.title : '' }))] }));
    }, [props.title, filterConfig, views, filters, showFilterPanel, selectedView, showViewsList, defaultViews, onViewChange, addViewsProps, hasViews, filterMode, advancedQuestion, advancedLoading, advancedPipeline, advancedError, showRefineModal, handleRefinementApplied, handleRestoreOriginal, runAdvancedSearch, runSavedPipeline, immediateUpdateFilters, pipelineRefined, originalPipeline]);
    const renderButton = (label, count, isActive, onClick) => {
        return _jsx("div", { className: classNames("uxpcore_osc__filters_container-button", {
                "uxpcore_osc__filters_container-button--active": isActive
            }), onClick: onClick, children: _jsx(Badge, { count: count, max: 9, position: 'right', children: label }) });
    };
    const resetChanges = useCallback(() => {
        hasFiltersChangedRef.current = false;
        hasSortChangedRef.current = false;
        hasColumnsChangedRef.current = false;
        setViewAction(null);
    }, []);
    const resetView = useCallback(() => {
        var _a, _b, _c, _d, _e, _f;
        setFilters(cleanFilters(((_a = selectedView === null || selectedView === void 0 ? void 0 : selectedView.configurations) === null || _a === void 0 ? void 0 : _a.filters) || { filters: {} }));
        setSort(cleanSort(((_b = selectedView === null || selectedView === void 0 ? void 0 : selectedView.configurations) === null || _b === void 0 ? void 0 : _b.sort) || {}));
        setSelectedColumns(((_c = selectedView === null || selectedView === void 0 ? void 0 : selectedView.configurations) === null || _c === void 0 ? void 0 : _c.columns) || []);
        setViewName((selectedView === null || selectedView === void 0 ? void 0 : selectedView.name) || '');
        // Clear advanced search state on cancel
        setFilterMode(((_d = selectedView === null || selectedView === void 0 ? void 0 : selectedView.configurations) === null || _d === void 0 ? void 0 : _d.filterMode) || 'simple');
        setAdvancedQuestion(((_f = (_e = selectedView === null || selectedView === void 0 ? void 0 : selectedView.configurations) === null || _e === void 0 ? void 0 : _e.advancedFilter) === null || _f === void 0 ? void 0 : _f.question) || '');
        setAdvancedPipeline(null);
        setAdvancedData(null);
        setExtraColumns([]);
        setOriginalPipelineHash(null);
        setShowRefineModal(false);
        setPipelineRefined(false);
        resetChanges();
    }, [selectedView, resetChanges]);
    const saveView = useCallback((forceUpdate, forceNew) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        const _view_name = (viewName || '').trim();
        if (!hasValue(_view_name)) {
            alerts.error('Name is required');
            return;
        }
        // forceUpdate=true  → update existing (Update View button)
        // forceNew=true     → create new (Save as New View button — no viewAction state change)
        // viewAction==='add'→ create new (from New Custom View in dropdown)
        // viewAction==='edit'→ update existing (fixes bug where this was creating new)
        const shouldCreateNew = forceNew || (!forceUpdate && viewAction === 'add');
        // Duplicate name check when creating new
        if (shouldCreateNew) {
            const existingNames = (((_a = viewsListRef.current) === null || _a === void 0 ? void 0 : _a.getViews()) || []).map(v => v.name.trim().toLowerCase());
            if (existingNames.includes(_view_name.toLowerCase())) {
                alerts.error(context.$L('uxp-core.text.view-name-duplicate'));
                return;
            }
        }
        const extraConfigs = { filterMode };
        if (filterMode === 'advanced' && advancedPipeline) {
            const fallbackHash = hashPipeline(advancedPipeline);
            extraConfigs.advancedFilter = {
                question: advancedQuestion,
                pipeline: advancedPipeline,
                pipelineHash: pipelineHash !== null && pipelineHash !== void 0 ? pipelineHash : fallbackHash,
                originalPipeline: originalPipeline !== null && originalPipeline !== void 0 ? originalPipeline : advancedPipeline,
                originalPipelineHash: originalPipelineHash !== null && originalPipelineHash !== void 0 ? originalPipelineHash : fallbackHash,
                isRefined: pipelineRefined,
            };
        }
        (_b = viewsListRef.current) === null || _b === void 0 ? void 0 : _b.save(shouldCreateNew, _view_name, filters, sort, selectedColumns, pageSize, extraConfigs).then(res => {
            if (res) {
                resetChanges();
            }
        });
    }), [viewName, filters, sort, selectedColumns, pageSize, viewAction, filterMode, advancedQuestion, advancedPipeline, pipelineHash, originalPipeline, originalPipelineHash, pipelineRefined, alerts, resetChanges, context]);
    const renderViewDetails = useCallback(() => {
        var _a, _b, _c, _d, _e, _f;
        const userFacingKeys = new Set(((filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.formFields) || []).flatMap(s => (s.fields || []).map((f) => f.name)));
        // dev-provided counter wins; else count formFields keys; fully-custom renders
        // have no formFields — count all applied keys instead of none
        const filtersCount = filterMode === 'advanced'
            ? (advancedPipeline ? 1 : 0)
            : (filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.getFilterCount)
                ? filterConfig.getFilterCount(filters)
                : getFilterCount(filters, userFacingKeys.size > 0 ? userFacingKeys : undefined);
        const sortCount = Object.entries(sort).reduce((count, [, value]) => (count += value !== 0 ? 1 : 0), 0);
        const isActionsColumnsEnabled = ((_a = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.actionsColumn) === null || _a === void 0 ? void 0 : _a.enable) || false;
        const canShowSortOption = isActionsColumnsEnabled && (((_c = (_b = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.actionsColumn) === null || _b === void 0 ? void 0 : _b.headerActions) === null || _c === void 0 ? void 0 : _c.sort) || false) && ((_d = Object.values(processedColumns || {})) === null || _d === void 0 ? void 0 : _d.some(pc => pc.isSortable));
        const canShowColumnCustomiseOption = ((_f = (_e = defaultActionColumns === null || defaultActionColumns === void 0 ? void 0 : defaultActionColumns.actionsColumn) === null || _e === void 0 ? void 0 : _e.headerActions) === null || _f === void 0 ? void 0 : _f.customiseColumns) || false;
        const hasAnyChanges = hasFiltersChangedRef.current || hasSortChangedRef.current || hasColumnsChangedRef.current;
        const showBar = !isDetailsPanelShowing && (addViewsProps === null || addViewsProps === void 0 ? void 0 : addViewsProps.enable) && (!!viewAction || hasAnyChanges);
        if (!showBar)
            return null;
        return _jsxs("div", { className: 'uxpcore_osc__filters_container', children: [_jsx("div", { className: 'uxpcore_osc__filters_container-input', children: _jsx(Input, { value: viewName || '', onChange: setViewName, placeholder: context.$L('uxp-core.text.enter-view-name') }) }), renderButton(context.$L('uxp-core.text.filters'), filtersCount, hasFiltersChangedRef.current, () => { setShowFilterPanel(true); }), canShowSortOption
                    ? renderButton(context.$L('uxp-core.text.sort-by'), sortCount, hasSortChangedRef.current, () => {
                        var _a;
                        (_a = actionsDropdwonRef.current) === null || _a === void 0 ? void 0 : _a.open();
                        setTimeout(() => { var _a; (_a = sortDropdwonRef.current) === null || _a === void 0 ? void 0 : _a.open(); }, 300);
                    })
                    : null, canShowColumnCustomiseOption
                    ? renderButton(context.$L('uxp-core.text.columns'), selectedColumns.length, hasColumnsChangedRef.current, () => {
                        var _a;
                        (_a = actionsDropdwonRef.current) === null || _a === void 0 ? void 0 : _a.open();
                        setTimeout(() => { var _a; (_a = columnsDropdwonRef.current) === null || _a === void 0 ? void 0 : _a.open(); }, 300);
                    })
                    : null, _jsxs("div", { className: 'uxpcore_osc__filters_container-buttons', children: [_jsx(Button, { title: context.$L('uxp-core.text.cancel'), onClick: resetView, leftIcon: 'fal times', variant: 'secondary' }), viewAction === 'add' && (_jsx(Button, { title: context.$L('uxp-core.text.save-view'), onClick: () => saveView(), leftIcon: 'fal save', variant: 'primary' })), viewAction === 'edit' && (_jsxs(_Fragment, { children: [_jsx(Button, { title: context.$L('uxp-core.text.save-view'), onClick: () => saveView(), leftIcon: 'fal save', variant: 'primary' }), _jsx(Button, { title: context.$L('uxp-core.text.save-as-new-view'), onClick: () => saveView(false, true), leftIcon: 'fal plus', variant: 'secondary' })] })), !viewAction && hasAnyChanges && (_jsxs(_Fragment, { children: [(selectedView === null || selectedView === void 0 ? void 0 : selectedView.isCustom) && (_jsx(Button, { title: context.$L('uxp-core.text.update-view'), onClick: () => saveView(true), leftIcon: 'fal save', variant: 'primary' })), _jsx(Button, { title: context.$L('uxp-core.text.save-as-new-view'), onClick: () => saveView(false, true), leftIcon: 'fal plus', variant: 'secondary' })] }))] })] });
    }, [saveView, resetView, defaultActionColumns, processedColumns, isDetailsPanelShowing, viewAction, viewName, selectedView, filters, sort, selectedColumns, filterMode, advancedPipeline, context, addViewsProps]);
    const renderDetailsPanel = useCallback(() => {
        if (!detailsPanel || !isDetailsPanelShowing)
            return null;
        const isCustomPanel = 'renderDetails' in detailsPanel;
        const onClose = () => {
            var _a;
            if (appendToURL) {
                const extraRemove = [];
                if (nameField)
                    extraRemove.push(urlParams.name);
                if (typeField)
                    extraRemove.push(urlParams.type);
                navigate(generateURL({ removeParams: [urlParams.key, ...(urlParams.key === 'objectKey' ? ['key'] : []), 'edit', urlParams.detailsTab, urlParams.additionalDetailsTab, ...extraRemove] }));
            }
            else {
                setViewInstance(null);
            }
            if (!isCustomPanel)
                (_a = detailsPanel.onClose) === null || _a === void 0 ? void 0 : _a.call(detailsPanel);
        };
        return (_jsx("div", { className: classNames("uxpcore_osc__details_container", {
                'uxpcore_osc__details_container--open': isDetailsPanelShowing
            }), style: { width: widths.details }, children: isCustomPanel
                ? detailsPanel.renderDetails(viewInstance, onClose)
                : _jsx(ObjectDetailsPanel, Object.assign({}, detailsPanel, { data: () => detailsPanel.data(viewInstance), onClose: onClose, appendToURL: appendToURL, urlParams: {
                        detailsTab: urlParams.detailsTab,
                        additionalDetailsTab: urlParams.additionalDetailsTab
                    } })) }));
    }, [detailsPanel, isDetailsPanelShowing, viewInstance, idField, nameField, typeField, widths.details, appendToURL, urlParams]);
    const totalWidth = useMemo(() => {
        if (!processedColumns)
            return '100%';
        return Object.values(processedColumns).reduce((total, { width }) => total + width, 0);
    }, [processedColumns]);
    return (_jsx("div", { className: classNames("uxpcore_osc__container", props.className), ref: containerRef, children: _jsxs("div", { className: "uxpcore_osc__content", children: [_jsx("div", { className: classNames("uxpcore_osc__list_container", {
                        'uxpcore_osc__list_container--has-details-panel': isDetailsPanelShowing
                    }), style: { width: widths.list }, ref: listContainerRef, children: (props.loading || !processedColumns) ? (_jsx(LoadingSpinner, {}))
                        : (_jsxs(_Fragment, { children: [(!!props.title || hasViews || !!props.actionButtons || !!filterConfig) && (_jsxs("div", { className: "uxpcore_osc__header", children: [renderTitle(), _jsx("div", { className: "uxpcore_osc__action_container", children: !isDetailsPanelShowing && props.actionButtons })] })), renderViewDetails(), !isDetailsPanelShowing && props.summaryContent && (_jsx("div", { className: "uxpcore_osc__summary_container", children: props.summaryContent })), _jsxs("div", { className: "uxpcore_osc__bulkaction_container", children: [!isDetailsPanelShowing && selected.length > 0 ? (_jsx("div", { className: "uxpcore_osc__bulkaction_container-left", children: props.bulkActionButtons })) : _jsx(_Fragment, {}), _jsxs("div", { className: "uxpcore_osc__bulkaction_container-right", children: [((_b = props === null || props === void 0 ? void 0 : props.search) === null || _b === void 0 ? void 0 : _b.enable) && (_jsx("div", { className: "uxpcore_osc__search_container", children: _jsx(SearchBox, { value: query, onChange: v => { setQuery(v); setPage(1); }, collapsed: ((_c = props === null || props === void 0 ? void 0 : props.search) === null || _c === void 0 ? void 0 : _c.collapsed) || false, position: 'left' }) })), _jsx(PaginationComponent, { total: total, page: page, pageSize: pageSize, dataLength: data.length, onPageChange: setPage, onPageSizeChange: ps => { setPageSize(ps); setPage(1); }, loading: loading, allowPageSizeChange: props.allowPageSizeChange })] })] }), _jsx(BaseTableComponent, Object.assign({}, props, { loading: loading, columns: processedColumns, data: data, totalWidth: totalWidth, handleResize: handleColumnResize, onClickRow: handleRowClick, onSelectRow: handleRowSelect, isRowSelected: isRowSelected, sort: sort, multiColumnSort: multiColumnSortEnabled, onChangeSort: handleSort }))] })) }), renderDetailsPanel()] }) }));
});
/**
 * @export
 * A component for searching, filtering, and displaying tabular data with support for pagination, views, and details panels.
 *
 * @example
 * ```
 * <ObjectSearchComponent
 *   title="Users"
 *   data={[{ id: '1', name: 'John' }, { id: '2', name: 'Jane' }]}
 *   columns={[{ id: 'name', label: 'Name' }]}
 *   pageSize={10}
 *   total={2}
 *   idField="id"
 * />
 * ```
 *
 * @example
 * ```
 * <ObjectSearchComponent
 *   title="Users"
 *   data={async (page, pageSize, query, filters, sort) => {
 *     return { items: [{ id: '1', name: 'John' }] };
 *   }}
 *   columns={[{ id: 'name', label: 'Name', isSortable: true, exportLabel: 'User Name' }]}
 *   pageSize={10}
 *   total={async () => 100}
 *   idField="id"
 *   filters={{ formFields: [{ name: 'name', label: 'Name', type: 'text' }] }}
 *   views={{ listId: 'users', defaultViews: [{ id: 'default', name: 'Default View' }], allowToMangeCustomViews: true }}
 *   defaultActionColumns={{
 *     selectColumn: { enable: true },
 *     actionsColumn: { enable: true, headerActions: { sort: true, customiseColumns: true } }
 *   }}
 *   search={{ enable: true, fields: ['name'] }}
 *   detailsPanel={{ renderDetails: (id, onClose) => <div>Details for {id}</div> }}
 *   collapsedWidth="40%"
 *   appendToURL={true}
 *   onSelect={(selected) => console.log('Selected rows:', selected)}
 * />
 * ```
 */
export const ObjectSearchComponent = memo(ObjectSearchComponentBase);
